import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Textarea } from "@/components/ui/textarea";
import {
  TrendingDown, AlertTriangle, PoundSterling, RefreshCw, Plus, Loader2,
  Calculator, FileSpreadsheet, Edit3, X, ChevronRight, ChevronDown, ChevronLeft, Search,
  MoreHorizontal, Target, CalendarRange, Info,
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  getBadDebtSummary, getBadDebtCases, createBadDebtCase, updateBadDebtCase,
  deleteBadDebtCase, logBadDebtPayment, upsertBadDebtProvision,
  getBadDebtPrincipalPreview, importBadDebtXlsx,
} from "@/lib/api";
import apiClient from "@/lib/api";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

// Sync endpoints aren't exported from api.js (yet) — use the raw client.
const triggerBadDebtSync = async () => (await apiClient.post("/legal/bad-debt/sync")).data;
const getBadDebtSyncStatus = async () => (await apiClient.get("/legal/bad-debt/sync/status")).data;
const auditXlsxVsLoanDisk = async () => (await apiClient.get("/legal/bad-debt/audit/xlsx-vs-loandisk")).data;
const backfillMonthsFromXlsx = async () => (await apiClient.post("/legal/bad-debt/backfill-months-from-xlsx")).data;
const seedUpcomingFromXlsx = async () => (await apiClient.post("/legal/bad-debt/seed-upcoming-from-xlsx")).data;
const searchLoanDisk = async (q) => (await apiClient.get(`/legal/bad-debt/loandisk/search?q=${encodeURIComponent(q)}`)).data;
const createCaseFromLoanDisk = async (loanRef, category) => (await apiClient.post(`/legal/bad-debt/cases/from-loandisk/${encodeURIComponent(loanRef)}?category=${category}`)).data;
const transferToBadDebt = async (caseId) => (await apiClient.post(`/legal/bad-debt/cases/${caseId}/transfer-to-bad-debt`)).data;
const restructureToCollections = async (caseId, target) => (await apiClient.post(`/legal/bad-debt/cases/${caseId}/restructure-to-collections?target_category=${target}`)).data;
const getBorrowerLoans = async (loanRef, fresh = false) => (await apiClient.get(`/legal/bad-debt/loandisk/borrower-loans/${encodeURIComponent(loanRef)}${fresh ? "?fresh=true" : ""}`)).data;
const relinkCaseToLoanDisk = async (caseId, loanApplicationId) => (await apiClient.post(`/legal/bad-debt/cases/${caseId}/relink-loandisk/${encodeURIComponent(loanApplicationId)}`)).data;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const fmtGBP = (n) => `£${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtGBPdp = (n) => `£${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (n) => `${((n || 0) * 100).toFixed(1)}%`;
const fmtMonth = (ym) => {
  if (!ym) return "—";
  const [y, m] = ym.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function BadDebtTracker() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [cases, setCases] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [selectedCase, setSelectedCase] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showProvision, setShowProvision] = useState(false);
  const [activeTab, setActiveTab] = useState("active");
  const [syncJob, setSyncJob] = useState(null);   // current/last sync job from backend
  const [syncing, setSyncing] = useState(false);
  const [auditReport, setAuditReport] = useState(null);  // xlsx ↔ LD reconciliation
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  // Date range filter — empty string means "no bound". Both inputs are
  // YYYY-MM (browser month picker). Defaults wide-open ("all"), but ops can
  // narrow to any custom window — e.g. a Jul→Jun fiscal year, a quarter, the
  // last 12 months — without us hard-coding any FY assumption.
  const [fromMonth, setFromMonth] = useState("");
  const [toMonth, setToMonth] = useState("");

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const [s, c, sj] = await Promise.all([
        getBadDebtSummary(),
        getBadDebtCases({ limit: 2000 }),
        getBadDebtSyncStatus(),
      ]);
      setSummary(s);
      setCases(c.cases || []);
      setSyncJob(sj.job || null);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load bad debt data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Poll sync status while a sync is in flight
  useEffect(() => {
    const inFlight = syncJob && ["queued", "processing"].includes(syncJob.status);
    if (!inFlight) return;
    const id = setInterval(async () => {
      try {
        const r = await getBadDebtSyncStatus();
        setSyncJob(r.job || null);
        if (r.job && r.job.status === "completed") {
          toast.success(`Sync done: ${r.job.processed} cases in ${Math.round(r.job.duration_seconds || 0)}s`);
          reload();
        }
        if (r.job && r.job.status === "failed") {
          toast.error("Sync failed: " + (r.job.error || "unknown"));
          setSyncing(false);
        }
      } catch (e) { /* swallow */ }
    }, 5000);
    return () => clearInterval(id);
  }, [syncJob, reload]);

  const activeCases = useMemo(() => cases.filter(c => c.category === "active"), [cases]);
  const upcomingCases = useMemo(() => cases.filter(c => c.category === "upcoming"), [cases]);

  // -------------------------------------------------------------------------
  // Period / date-range filter
  //
  // Two design choices that drove this implementation:
  //   1. Custom from/to picker (not a calendar-year dropdown). Ops's fiscal
  //      year is Jul→Jun and may change in future — hard-coding "2025" /
  //      "2024" buckets would age out quickly. Free-form month range works
  //      for any FY definition and any ad-hoc window (a quarter, last 12m).
  //   2. The months axis is *extended forward* by EXTEND_MONTHS_AHEAD past
  //      today so as time progresses the matrix automatically grows new
  //      empty columns for upcoming write-offs to land in — no more axis
  //      "falling off" at the current month.
  // -------------------------------------------------------------------------
  const today = useMemo(() => new Date(), []);
  const currentYM = useMemo(
    () => `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`,
    [today]
  );

  // Helpers
  const ymAdd = useCallback((ym, deltaMonths) => {
    const [y, m] = ym.split("-").map(Number);
    const d = new Date(y, m - 1 + deltaMonths, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }, []);

  // Months data from the backend roll-up (always populated with real numbers)
  const backendMonths = useMemo(() => summary?.months || [], [summary]);
  const backendMonthsByYM = useMemo(() => {
    const map = new Map();
    backendMonths.forEach(m => map.set(m.month, m));
    return map;
  }, [backendMonths]);

  // Forward-extend the axis: stretch from the earliest backend month (or 12mo
  // ago if no data yet) through current month + EXTEND_MONTHS_AHEAD. Each
  // synthetic future month inherits the last-known cumulative totals + pool
  // balance so the chart's "Remaining Provision" line carries forward
  // sensibly instead of cliff-edging to zero.
  const EXTEND_MONTHS_AHEAD = 6;
  const extendedMonthsData = useMemo(() => {
    if (backendMonths.length === 0 && !summary) return [];
    const earliest = backendMonths[0]?.month || ymAdd(currentYM, -12);
    const lastBackend = backendMonths[backendMonths.length - 1];
    const endYM = ymAdd(currentYM, EXTEND_MONTHS_AHEAD);
    const result = [];
    let cursor = earliest;
    // Carry-forward state for synthetic future months
    let lastRem = lastBackend?.remaining_provision ?? 0;
    let lastCumDebt = lastBackend?.total_bad_debt ?? 0;
    let lastCumProv = lastBackend?.total_provision ?? 0;
    let lastCumPct = lastBackend?.total_provision_pct ?? 0;
    while (cursor <= endYM) {
      const existing = backendMonthsByYM.get(cursor);
      if (existing) {
        result.push(existing);
        lastRem = existing.remaining_provision ?? lastRem;
        lastCumDebt = existing.total_bad_debt ?? lastCumDebt;
        lastCumProv = existing.total_provision ?? lastCumProv;
        lastCumPct = existing.total_provision_pct ?? lastCumPct;
      } else {
        result.push({
          month: cursor,
          monthly_bad_debt: 0,
          monthly_provision: 0,
          monthly_burn_pct: 0,
          remaining_provision: lastRem,
          total_bad_debt: lastCumDebt,
          total_provision: lastCumProv,
          total_provision_pct: lastCumPct,
          _synthetic: true,  // marker for downstream consumers if needed
        });
      }
      cursor = ymAdd(cursor, 1);
    }
    return result;
  }, [backendMonths, backendMonthsByYM, summary, currentYM, ymAdd]);

  const allMonths = useMemo(() => extendedMonthsData.map(m => m.month), [extendedMonthsData]);

  // Range derivation
  const periodRange = useMemo(() => {
    if (!fromMonth && !toMonth) return null;
    const start = fromMonth || allMonths[0] || "0000-00";
    const end = toMonth || allMonths[allMonths.length - 1] || "9999-12";
    return { start, end, label: `${fmtMonth(start)} → ${fmtMonth(end)}` };
  }, [fromMonth, toMonth, allMonths]);

  const visibleMonthsData = useMemo(() => {
    if (!periodRange) return extendedMonthsData;
    return extendedMonthsData.filter(m => m.month >= periodRange.start && m.month <= periodRange.end);
  }, [extendedMonthsData, periodRange]);

  const visibleMonths = useMemo(() => visibleMonthsData.map(m => m.month), [visibleMonthsData]);

  const inPeriod = useCallback((ym) => {
    if (!periodRange) return true;
    if (!ym) return false;
    return ym >= periodRange.start && ym <= periodRange.end;
  }, [periodRange]);

  const filteredActive = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    let rows = activeCases;
    if (periodRange) rows = rows.filter(c => inPeriod(c.write_off_month));
    if (q) {
      rows = rows.filter(c =>
        (c.customer_name || "").toLowerCase().includes(q) ||
        (c.sfa_number || "").toLowerCase().includes(q)
      );
    }
    return rows;
  }, [activeCases, filterText, periodRange, inPeriod]);

  const filteredUpcoming = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    let rows = upcomingCases;
    if (periodRange) rows = rows.filter(c => !c.write_off_month || inPeriod(c.write_off_month));
    if (q) {
      rows = rows.filter(c =>
        (c.customer_name || "").toLowerCase().includes(q) ||
        (c.sfa_number || "").toLowerCase().includes(q)
      );
    }
    return rows;
  }, [upcomingCases, filterText, periodRange, inPeriod]);

  // Matrix axis = visible months (filtered + future-extended).
  const monthsAxis = visibleMonths;

  // Quick-preset handlers — applied via state instead of derived so the
  // From/To inputs reflect what's active, and ops can hand-edit afterwards.
  const applyCurrentFY = useCallback(() => {
    // Fiscal year July → June. If today is in Jul–Dec, FY = thisYear-Jul → nextYear-Jun.
    // If today is in Jan–Jun, FY = lastYear-Jul → thisYear-Jun.
    const y = today.getFullYear();
    const startY = today.getMonth() + 1 >= 7 ? y : y - 1;
    setFromMonth(`${startY}-07`);
    setToMonth(`${startY + 1}-06`);
  }, [today]);

  const applyLastFY = useCallback(() => {
    const y = today.getFullYear();
    const startY = (today.getMonth() + 1 >= 7 ? y : y - 1) - 1;
    setFromMonth(`${startY}-07`);
    setToMonth(`${startY + 1}-06`);
  }, [today]);

  const applyLast12 = useCallback(() => {
    setFromMonth(ymAdd(currentYM, -11));
    setToMonth(currentYM);
  }, [currentYM, ymAdd]);

  const clearRange = useCallback(() => {
    setFromMonth("");
    setToMonth("");
  }, []);

  const handleSync = async () => {
    try {
      setSyncing(true);
      const r = await triggerBadDebtSync();
      setSyncJob(r.job);
      toast.info("LoanDisk sync started — this typically takes 3-4 minutes for ~100 legal loans");
    } catch (e) {
      toast.error("Sync failed: " + errorDetailMessage(e, e.message));
      setSyncing(false);
    }
  };

  const handleImport = async () => {
    if (!confirm("Re-seed monthly provisions from the bundled Google Sheets export? Live LoanDisk cases are not touched.")) return;
    try {
      const r = await importBadDebtXlsx(false);
      toast.success(`Provisions upserted: ${r.provisions_upserted}`);
      reload();
    } catch (e) {
      toast.error("Import failed: " + errorDetailMessage(e, e.message));
    }
  };

  const handleRunAudit = async () => {
    try {
      setAuditLoading(true);
      const r = await auditXlsxVsLoanDisk();
      setAuditReport(r);
      setAuditOpen(true);
    } catch (e) {
      toast.error("Audit failed: " + errorDetailMessage(e, e.message));
    } finally { setAuditLoading(false); }
  };

  const handleBackfill = async () => {
    if (!confirm("Backfill write-off months and partial-settlement default %s from xlsx onto matching LoanDisk cases? Skips ambiguous/low-confidence matches.")) return;
    try {
      const r = await backfillMonthsFromXlsx();
      toast.success(`Backfilled ${r.applied_count} cases · ${r.unmatched_xlsx_count} missing from LoanDisk`);
      reload();
    } catch (e) {
      toast.error("Backfill failed: " + errorDetailMessage(e, e.message));
    }
  };

  const handleSeedUpcoming = async () => {
    try {
      const r = await seedUpcomingFromXlsx();
      toast.success(`Inserted ${r.inserted} upcoming-capital-at-risk cases · ${r.skipped} already existed`);
      reload();
    } catch (e) {
      toast.error("Seed upcoming failed: " + errorDetailMessage(e, e.message));
    }
  };

  // Format last-sync timestamp nicely
  const lastSyncText = useMemo(() => {
    if (!syncJob) return "never synced";
    const t = syncJob.finished_at || syncJob.started_at;
    if (!t) return "—";
    const diffSec = Math.round((Date.now() - new Date(t).getTime()) / 1000);
    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
    return `${Math.round(diffSec / 86400)}d ago`;
  }, [syncJob]);

  const syncInFlight = syncJob && ["queued", "processing"].includes(syncJob.status);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <TooltipProvider>
      <div className="space-y-6 pb-12 min-w-0 max-w-full" data-testid="bad-debt-tracker">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Bad Debt Tracker</h1>
            <p className="text-muted-foreground text-sm">
              UK SF Capital · live mirror of the legal write-off pipeline + monthly provision pool
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {syncInFlight ? (
                <span className="text-blue-700 inline-flex items-center gap-1.5" data-testid="bd-sync-progress">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  LoanDisk sync running… {syncJob?.processed || 0} / {syncJob?.total || "?"} cases · {syncJob?.phase || syncJob?.status}
                </span>
              ) : (
                <span data-testid="bd-last-sync">
                  Last LoanDisk sync: <strong>{lastSyncText}</strong>
                  {syncJob?.status === "completed" && ` · ${syncJob.processed} cases in ${Math.round(syncJob.duration_seconds || 0)}s`}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={handleSync}
              disabled={syncInFlight}
              data-testid="bd-sync-btn"
              className="bg-blue-600 hover:bg-blue-700"
            >
              {syncInFlight ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Sync from LoanDisk
            </Button>
            <Button size="sm" onClick={() => setShowAdd(true)} data-testid="bd-add-case-btn" className="bg-red-600 hover:bg-red-700">
              <Plus className="w-4 h-4 mr-1.5" />
              Add Case
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="bd-more-menu-btn">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs">Utilities</DropdownMenuLabel>
                <DropdownMenuItem onClick={reload} data-testid="bd-refresh-menu-item">
                  <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? "animate-spin" : ""}`} />
                  Refresh view
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs">Data Audit</DropdownMenuLabel>
                <DropdownMenuItem onClick={handleRunAudit} disabled={auditLoading} data-testid="bd-audit-menu-item">
                  {auditLoading ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5 mr-2" />}
                  Reconcile xlsx vs LoanDisk
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleImport} data-testid="bd-reseed-menu-item">
                  <FileSpreadsheet className="w-3.5 h-3.5 mr-2" />
                  Re-seed provisions from xlsx
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KpiCard label="Total Bad Debt"        value={fmtGBP(summary?.kpis?.total_bad_debt)}        sub="cumulative"  color="red"      icon={TrendingDown} />
          <KpiCard label="Total Provision"       value={fmtGBP(summary?.kpis?.total_provision)}       sub="cumulative"  color="emerald"  icon={PoundSterling} />
          <KpiCard label="Remaining Provision"   value={fmtGBP(summary?.kpis?.remaining_provision)}   sub="pool balance" color="blue"     icon={PoundSterling} />
          <KpiCard label="Provision Utilisation" value={fmtPct(summary?.kpis?.total_provision_utilisation_pct)} sub={`${summary?.kpis?.active_case_count || 0} active · ${summary?.kpis?.upcoming_case_count || 0} upcoming · £${(summary?.kpis?.total_upcoming_at_risk || 0).toLocaleString()} at risk`} color="amber" icon={AlertTriangle} />
        </div>

        {/* Data audit panel — only renders if the audit has been run */}
        {auditReport && (
          <AuditPanel
            report={auditReport}
            open={auditOpen}
            onToggle={() => setAuditOpen(!auditOpen)}
            onBackfill={handleBackfill}
            onSeedUpcoming={handleSeedUpcoming}
            onClose={() => setAuditReport(null)}
          />
        )}

        {/* Budget planner — provision pool management. Slim, inline,
            replaces the previous toolbar "Monthly Provision" button. */}
        <BudgetPlannerCard
          months={summary?.months || []}
          kpis={summary?.kpis}
          onOpenFullDialog={() => setShowProvision(true)}
        />

        {/* Monthly roll-up table */}
        <MonthlyRollUp months={visibleMonthsData} loading={loading} periodLabel={periodRange?.label} />

        {/* Filter row — search + date range presets + custom from/to. */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />
              <Input
                placeholder="Filter by customer or SF#…"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="pl-9"
                data-testid="bd-filter-input"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <CalendarRange className="w-4 h-4 text-muted-foreground" />
              <div className="flex items-center gap-1.5 text-xs">
                <Label className="text-[11px] text-muted-foreground">From</Label>
                <MonthPicker
                  value={fromMonth}
                  onChange={setFromMonth}
                  placeholder="Start month"
                  maxMonth={toMonth || undefined}
                  testId="bd-period-from"
                />
                <Label className="text-[11px] text-muted-foreground">To</Label>
                <MonthPicker
                  value={toMonth}
                  onChange={setToMonth}
                  placeholder="End month"
                  minMonth={fromMonth || undefined}
                  testId="bd-period-to"
                />
                {(fromMonth || toMonth) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearRange}
                    className="h-7 px-2 text-[11px]"
                    data-testid="bd-period-clear"
                  >
                    <X className="w-3 h-3 mr-1" /> Clear
                  </Button>
                )}
              </div>
            </div>
          </div>
          {/* Quick presets — FY anchor is Jul→Jun today but can be changed
              by hand-editing the From/To inputs above. If your FY changes,
              just rebind these handlers in one place. */}
          <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
            <span className="text-muted-foreground">Quick:</span>
            <Button variant="outline" size="sm" className="h-7 px-2 text-[11px]" onClick={applyCurrentFY} data-testid="bd-preset-current-fy">This FY (Jul–Jun)</Button>
            <Button variant="outline" size="sm" className="h-7 px-2 text-[11px]" onClick={applyLastFY} data-testid="bd-preset-last-fy">Last FY</Button>
            <Button variant="outline" size="sm" className="h-7 px-2 text-[11px]" onClick={applyLast12} data-testid="bd-preset-last-12">Last 12 months</Button>
            {periodRange && (
              <Badge variant="outline" className="text-[10px] font-normal ml-1" data-testid="bd-period-badge">
                {periodRange.label} · {visibleMonths.length} months · {filteredActive.length} active · {filteredUpcoming.length} upcoming
              </Badge>
            )}
          </div>
        </div>

        {/* Tabs: Active vs Upcoming */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="active" data-testid="bd-tab-active">
              Active Bad Debt · {filteredActive.length}{periodRange && activeCases.length !== filteredActive.length ? ` / ${activeCases.length}` : ""}
            </TabsTrigger>
            <TabsTrigger value="upcoming" data-testid="bd-tab-upcoming">
              Upcoming Capital at Risk · {filteredUpcoming.length}{periodRange && upcomingCases.length !== filteredUpcoming.length ? ` / ${upcomingCases.length}` : ""}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active" className="mt-4">
            <CasesMatrix
              rows={filteredActive}
              monthsAxis={monthsAxis}
              onSelect={setSelectedCase}
              emptyText={periodRange ? `No active bad-debt cases written off in ${periodRange.label}.` : "No active bad-debt cases yet — click Sync from LoanDisk."}
              testIdPrefix="bd-active"
              autoScrollMonth={periodRange ? periodRange.start : currentYM}
            />
          </TabsContent>

          <TabsContent value="upcoming" className="mt-4 space-y-4">
            <LoanDiskSearchPanel onCaseAdded={reload} />
            <CasesMatrix
              rows={filteredUpcoming}
              monthsAxis={monthsAxis}
              onSelect={setSelectedCase}
              emptyText="No upcoming-capital-at-risk cases. Use the search above to add one from LoanDisk."
              testIdPrefix="bd-upcoming"
              tone="amber"
              autoScrollMonth={periodRange ? periodRange.start : currentYM}
            />
          </TabsContent>
        </Tabs>

        {/* Side drawer for selected case */}
        <CaseDrawer
          caseRow={selectedCase}
          open={!!selectedCase}
          onClose={() => setSelectedCase(null)}
          onChanged={() => { reload(); setSelectedCase(null); }}
        />

        {/* Add-case dialog */}
        <AddCaseDialog open={showAdd} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); reload(); }} />

        {/* Provision dialog */}
        <ProvisionDialog open={showProvision} onClose={() => setShowProvision(false)} onSaved={() => { setShowProvision(false); reload(); }} />
      </div>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// KPI card
// ---------------------------------------------------------------------------
// MonthPicker — click-to-open popover with year navigation + 4×3 month grid.
//
// Shadcn's <Calendar> is day-grained, but bad-debt accounting is always month-
// grained (write_off_month, monthly_provision, etc.). A day picker would just
// add noise. This component speaks YYYY-MM strings directly so it slots into
// the existing periodRange state with no parsing required.
// ---------------------------------------------------------------------------
function MonthPicker({ value, onChange, placeholder = "Pick month", minMonth, maxMonth, testId }) {
  const [open, setOpen] = useState(false);
  // Anchor year shown in the popover. Defaults to selected value's year, then
  // today, so opening an empty picker lands on the current year.
  const initialYear = value ? Number(value.split("-")[0]) : new Date().getFullYear();
  const [year, setYear] = useState(initialYear);

  // Re-anchor when value changes from outside (e.g. preset button click)
  useEffect(() => {
    if (value) setYear(Number(value.split("-")[0]));
  }, [value]);

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const selectedMonth = value && Number(value.split("-")[0]) === year ? Number(value.split("-")[1]) : null;

  const displayLabel = value
    ? (() => {
        const [y, m] = value.split("-");
        return `${monthNames[Number(m) - 1]} ${y.slice(-2)}`;
      })()
    : placeholder;

  const isOutOfBounds = (ym) => {
    if (minMonth && ym < minMonth) return true;
    if (maxMonth && ym > maxMonth) return true;
    return false;
  };

  const pick = (monthIdx) => {
    const ym = `${year}-${String(monthIdx + 1).padStart(2, "0")}`;
    if (isOutOfBounds(ym)) return;
    onChange(ym);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`h-8 w-[120px] justify-start text-xs font-normal ${!value ? "text-muted-foreground" : ""}`}
          data-testid={testId}
        >
          <CalendarRange className="w-3.5 h-3.5 mr-1.5 shrink-0" />
          {displayLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-3" align="start">
        {/* Year navigator */}
        <div className="flex items-center justify-between mb-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setYear(y => y - 1)}
            data-testid={testId ? `${testId}-prev-year` : undefined}
            aria-label="Previous year"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="font-semibold text-sm" data-testid={testId ? `${testId}-year` : undefined}>{year}</div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setYear(y => y + 1)}
            data-testid={testId ? `${testId}-next-year` : undefined}
            aria-label="Next year"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        {/* Month grid */}
        <div className="grid grid-cols-3 gap-1.5">
          {monthNames.map((name, idx) => {
            const ym = `${year}-${String(idx + 1).padStart(2, "0")}`;
            const disabled = isOutOfBounds(ym);
            const selected = selectedMonth === idx + 1;
            return (
              <button
                key={name}
                type="button"
                disabled={disabled}
                onClick={() => pick(idx)}
                className={`h-9 rounded-md text-xs font-medium transition-colors ${
                  selected
                    ? "bg-blue-600 text-white"
                    : disabled
                      ? "text-muted-foreground/40 cursor-not-allowed"
                      : "hover:bg-slate-100 text-foreground"
                }`}
                data-testid={testId ? `${testId}-${name.toLowerCase()}` : undefined}
              >
                {name}
              </button>
            );
          })}
        </div>
        {/* Quick: jump to current month */}
        <div className="flex justify-between items-center mt-3 pt-2 border-t">
          <button
            type="button"
            className="text-[11px] text-blue-600 hover:underline"
            onClick={() => {
              const now = new Date();
              setYear(now.getFullYear());
              pick(now.getMonth());
            }}
            data-testid={testId ? `${testId}-today` : undefined}
          >
            This month
          </button>
          {value && (
            <button
              type="button"
              className="text-[11px] text-muted-foreground hover:text-foreground"
              onClick={() => { onChange(""); setOpen(false); }}
              data-testid={testId ? `${testId}-clear` : undefined}
            >
              Clear
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}


// ---------------------------------------------------------------------------
function KpiCard({ label, value, sub, color = "slate", icon: Icon }) {
  const tones = {
    red:     "bg-red-50 text-red-600",
    emerald: "bg-emerald-50 text-emerald-600",
    blue:    "bg-blue-50 text-blue-600",
    amber:   "bg-amber-50 text-amber-600",
  };
  return (
    <Card data-testid={`bd-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>
          </div>
          {Icon && (
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tones[color] || tones.amber}`}>
              <Icon className="w-5 h-5" />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Monthly roll-up — collapsible card + grouped bar chart + table.
// ---------------------------------------------------------------------------
function MonthlyRollUp({ months, loading, periodLabel }) {
  const [open, setOpen] = useState(true);
  const [view, setView] = useState("chart");  // chart | table

  return (
    <Card>
      <CardHeader className="pb-3 cursor-pointer select-none" onClick={() => setOpen(!open)} data-testid="bd-rollup-header">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Calculator className="w-4 h-4" />
            Monthly Roll-up
            <span className="text-xs font-normal text-muted-foreground ml-2">
              {months.length} months{periodLabel ? ` · ${periodLabel}` : ""} · burn % &gt; 100 highlighted red
            </span>
          </CardTitle>
          <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
            {open && (
              <div className="flex gap-0.5 border rounded-md overflow-hidden text-xs">
                <button
                  className={`px-2.5 py-1 ${view === "chart" ? "bg-blue-600 text-white" : "bg-white hover:bg-slate-100"}`}
                  onClick={() => setView("chart")}
                  data-testid="bd-rollup-view-chart"
                >Chart</button>
                <button
                  className={`px-2.5 py-1 ${view === "table" ? "bg-blue-600 text-white" : "bg-white hover:bg-slate-100"}`}
                  onClick={() => setView("table")}
                  data-testid="bd-rollup-view-table"
                >Table</button>
              </div>
            )}
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </div>
        </div>
      </CardHeader>
      {open && (
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-10"><Loader2 className="w-4 h-4 animate-spin inline" /> loading…</div>
          ) : months.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground text-sm">No data yet — click Sync from LoanDisk.</div>
          ) : view === "chart" ? (
            <MonthlyRollUpChart months={months} />
          ) : (
            <MonthlyRollUpTable months={months} />
          )}
        </CardContent>
      )}
    </Card>
  );
}

// SVG-based grouped bar chart showing Monthly Bad Debt (red) vs Monthly
// Provision (emerald) with a Remaining Provision line overlay.
function MonthlyRollUpChart({ months }) {
  const [hover, setHover] = useState(null);  // { month_obj, x, y }
  const W = 1100, H = 320, PAD_L = 60, PAD_R = 30, PAD_T = 20, PAD_B = 50;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const n = months.length;
  const slotW = innerW / Math.max(n, 1);
  const barW = Math.min(slotW * 0.36, 20);
  const maxVal = Math.max(
    ...months.map(m => Math.max(m.monthly_bad_debt, m.monthly_provision, 1)),
    1,
  );
  const niceMax = Math.ceil(maxVal / 50000) * 50000;
  const y = (v) => PAD_T + innerH - (v / niceMax) * innerH;
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(f => f * niceMax);
  const remMin = Math.min(...months.map(m => m.remaining_provision), 0);
  const remMax = Math.max(...months.map(m => m.remaining_provision), 0);
  const remRange = remMax - remMin || 1;
  const yRem = (v) => PAD_T + innerH - ((v - remMin) / remRange) * innerH;

  const linePath = months.map((m, i) => {
    const cx = PAD_L + i * slotW + slotW / 2;
    return `${i === 0 ? "M" : "L"} ${cx.toFixed(1)} ${yRem(m.remaining_provision).toFixed(1)}`;
  }).join(" ");

  return (
    <div className="px-4 pb-4 pt-2 overflow-x-auto relative" data-testid="bd-rollup-chart">
      <div className="flex gap-4 text-[11px] text-muted-foreground mb-2 pl-2">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-red-500" /> Monthly Bad Debt</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-500" /> Monthly Provision</span>
        <span className="flex items-center gap-1.5"><span className="w-4 h-0.5 bg-blue-600" /> Remaining Provision (pool balance)</span>
      </div>
      <div className="relative">
        <svg width={W} height={H} className="font-sans">
          {/* Grid + y labels */}
          {yTicks.map((t, i) => (
            <g key={i}>
              <line x1={PAD_L} x2={W - PAD_R} y1={y(t)} y2={y(t)} stroke="#e5e7eb" strokeWidth="1" />
              <text x={PAD_L - 8} y={y(t) + 3} fontSize="10" textAnchor="end" fill="#6b7280">
                £{t >= 1000 ? `${(t / 1000).toFixed(0)}k` : t.toFixed(0)}
              </text>
            </g>
          ))}
          {/* Bars + invisible hover hitboxes */}
          {months.map((m, i) => {
            const slotX = PAD_L + i * slotW;
            const cx = slotX + slotW / 2;
            const barH1 = innerH - (y(m.monthly_bad_debt) - PAD_T);
            const barH2 = innerH - (y(m.monthly_provision) - PAD_T);
            return (
              <g key={m.month}>
                <rect x={cx - barW - 1} y={y(m.monthly_bad_debt)} width={barW} height={Math.max(barH1, 0)} fill="#ef4444" opacity={0.85} />
                <rect x={cx + 1} y={y(m.monthly_provision)} width={barW} height={Math.max(barH2, 0)} fill="#10b981" opacity={0.85} />
                {m.monthly_burn_pct > 1 && (
                  <text x={cx} y={y(Math.max(m.monthly_bad_debt, m.monthly_provision)) - 4} fontSize="9" textAnchor="middle" fill="#dc2626" fontWeight="600">
                    {Math.round(m.monthly_burn_pct * 100)}%
                  </text>
                )}
                {(n <= 16 || i % 2 === 0) && (
                  <text x={cx} y={H - PAD_B + 14} fontSize="9.5" textAnchor="middle" fill="#475569">
                    {fmtMonth(m.month)}
                  </text>
                )}
                {/* Invisible wide hitbox for hover — covers the whole slot */}
                <rect
                  x={slotX} y={PAD_T} width={slotW} height={innerH}
                  fill="transparent"
                  onMouseEnter={() => setHover({ m, cx, cy: PAD_T })}
                  onMouseLeave={() => setHover(null)}
                  data-testid={`bd-rollup-hover-${m.month}`}
                />
              </g>
            );
          })}
          {/* Remaining Provision line */}
          <path d={linePath} fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" pointerEvents="none" />
          {months.map((m, i) => (
            <circle key={`pt-${m.month}`} cx={PAD_L + i * slotW + slotW / 2} cy={yRem(m.remaining_provision)} r="2.5" fill="#2563eb" pointerEvents="none" />
          ))}
          {/* Hover marker line */}
          {hover && (
            <line x1={hover.cx} x2={hover.cx} y1={PAD_T} y2={H - PAD_B} stroke="#475569" strokeWidth="1" strokeDasharray="2 3" pointerEvents="none" />
          )}
          <line x1={PAD_L} x2={W - PAD_R} y1={H - PAD_B} y2={H - PAD_B} stroke="#94a3b8" strokeWidth="1" />
        </svg>

        {/* Hover tooltip — positioned absolutely over the SVG */}
        {hover && (
          <div
            className="absolute pointer-events-none bg-slate-900 text-white rounded-md shadow-lg px-3 py-2 text-xs z-10"
            style={{
              left: Math.min(hover.cx + 12, W - 220),
              top: hover.cy + 4,
              minWidth: 200,
            }}
            data-testid="bd-rollup-tooltip"
          >
            <div className="font-semibold mb-1">{fmtMonth(hover.m.month)}</div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-red-400" />Bad Debt</span>
              <span className="text-right tabular-nums">{fmtGBP(hover.m.monthly_bad_debt)}</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-emerald-400" />Provision</span>
              <span className="text-right tabular-nums">{fmtGBP(hover.m.monthly_provision)}</span>
              <span>Burn %</span>
              <span className={`text-right tabular-nums ${hover.m.monthly_burn_pct > 1 ? "text-red-300 font-semibold" : ""}`}>{fmtPct(hover.m.monthly_burn_pct)}</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-0.5 bg-blue-400" />Remaining pool</span>
              <span className={`text-right tabular-nums ${hover.m.remaining_provision < 0 ? "text-red-300" : ""}`}>{fmtGBP(hover.m.remaining_provision)}</span>
              <span className="border-t border-slate-700 pt-0.5 mt-0.5">Cum. Bad Debt</span>
              <span className="text-right tabular-nums border-t border-slate-700 pt-0.5 mt-0.5">{fmtGBP(hover.m.total_bad_debt)}</span>
              <span>Cum. Provision</span>
              <span className="text-right tabular-nums">{fmtGBP(hover.m.total_provision)}</span>
              <span>Util %</span>
              <span className="text-right tabular-nums">{fmtPct(hover.m.total_provision_pct)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MonthlyRollUpTable({ months }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="bd-monthly-table">
        <thead className="bg-slate-50 text-muted-foreground uppercase">
          <tr>
            <th className="text-left  px-3 py-2 font-semibold">Month</th>
            <th className="text-right px-3 py-2 font-semibold">Monthly Bad Debt</th>
            <th className="text-right px-3 py-2 font-semibold">Monthly Provision</th>
            <th className="text-right px-3 py-2 font-semibold">Monthly Burn %</th>
            <th className="text-right px-3 py-2 font-semibold">Remaining Provision</th>
            <th className="text-right px-3 py-2 font-semibold">Total Bad Debt</th>
            <th className="text-right px-3 py-2 font-semibold">Total Provision</th>
            <th className="text-right px-3 py-2 font-semibold">Total Provision %</th>
          </tr>
        </thead>
        <tbody>
          {months.map(m => {
            const burnHigh = m.monthly_burn_pct > 1;
            return (
              <tr key={m.month} className="border-t hover:bg-slate-50">
                <td className="px-3 py-2 font-medium">{fmtMonth(m.month)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-red-700">{fmtGBP(m.monthly_bad_debt)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtGBP(m.monthly_provision)}</td>
                <td className={`px-3 py-2 text-right tabular-nums ${burnHigh ? "text-red-700 font-semibold" : ""}`}>{fmtPct(m.monthly_burn_pct)}</td>
                <td className={`px-3 py-2 text-right tabular-nums ${m.remaining_provision < 0 ? "text-red-700" : "text-emerald-700"}`}>{fmtGBP(m.remaining_provision)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtGBP(m.total_bad_debt)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtGBP(m.total_provision)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtPct(m.total_provision_pct)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cases table
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Budget Planner — compact inline card surfacing the bad-debt provision pool
// (7%-of-originations) in a less spreadsheet-y way. Replaces the old
// "Monthly Provision" toolbar button. Click the cog to open the full
// month-by-month dialog.
// ---------------------------------------------------------------------------
function BudgetPlannerCard({ months, kpis, onOpenFullDialog }) {
  // Most recent month with a published provision
  const lastProvision = useMemo(() => {
    if (!months?.length) return null;
    for (let i = months.length - 1; i >= 0; i--) {
      if ((months[i].monthly_provision || 0) > 0) return months[i];
    }
    return null;
  }, [months]);

  // Current calendar month — does a provision exist for it yet?
  const currentMonthKey = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }, []);
  const currentMonthRow = months?.find(m => m.month === currentMonthKey);
  const currentProvisionSet = (currentMonthRow?.monthly_provision || 0) > 0;

  return (
    <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-white" data-testid="bd-budget-planner">
      <CardContent className="py-4 px-5">
        <div className="flex items-center justify-between gap-6 flex-wrap">
          {/* Identity / icon */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Target className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">Bad Debt Budget Planner</h3>
              <p className="text-[11px] text-muted-foreground">7% of monthly originations · feeds the provision pool</p>
            </div>
          </div>

          {/* Quick stats */}
          <div className="flex items-center gap-6 text-xs">
            <div>
              <div className="text-[10px] uppercase text-muted-foreground tracking-wide">Pool balance</div>
              <div className={`font-semibold text-base ${(kpis?.remaining_provision || 0) < 0 ? "text-red-700" : "text-emerald-700"}`}>
                {fmtGBP(kpis?.remaining_provision)}
              </div>
            </div>
            <div className="border-l pl-6">
              <div className="text-[10px] uppercase text-muted-foreground tracking-wide">Last published</div>
              <div className="font-semibold text-base">
                {lastProvision ? `${fmtGBP(lastProvision.monthly_provision)}` : "—"}
                <span className="text-[10px] font-normal text-muted-foreground ml-1">({lastProvision ? fmtMonth(lastProvision.month) : "none yet"})</span>
              </div>
            </div>
            <div className="border-l pl-6">
              <div className="text-[10px] uppercase text-muted-foreground tracking-wide">This month</div>
              <div className={`font-semibold text-base ${currentProvisionSet ? "text-emerald-700" : "text-amber-700"}`}>
                {currentProvisionSet ? fmtGBP(currentMonthRow.monthly_provision) : "Not set"}
              </div>
            </div>
          </div>

          {/* Action */}
          <Button
            size="sm"
            variant={currentProvisionSet ? "outline" : "default"}
            onClick={onOpenFullDialog}
            data-testid="bd-budget-set-btn"
            className={!currentProvisionSet ? "bg-emerald-600 hover:bg-emerald-700" : ""}
          >
            {currentProvisionSet ? (
              <><Edit3 className="w-3.5 h-3.5 mr-1.5" /> Adjust month</>
            ) : (
              <><Plus className="w-3.5 h-3.5 mr-1.5" /> Set {fmtMonth(currentMonthKey)} provision</>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// LoanDisk search panel — used in the Upcoming tab to add new capital-at-risk
// cases by searching live LoanDisk loans (any status: Active, Past Due, etc.).
// ---------------------------------------------------------------------------
function LoanDiskSearchPanel({ onCaseAdded }) {
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const runSearch = async (e) => {
    e?.preventDefault?.();
    if (!q.trim() || q.trim().length < 2) {
      toast.error("Type at least 2 characters");
      return;
    }
    try {
      setSearching(true);
      const r = await searchLoanDisk(q.trim());
      setResults(r.results || []);
      if ((r.results || []).length === 0) toast.info("No LoanDisk matches");
    } catch (e) {
      toast.error("LoanDisk search failed: " + errorDetailMessage(e, e.message));
    } finally { setSearching(false); }
  };

  const addAsUpcoming = async (row) => {
    try {
      setBusyId(row.loan_id);
      const r = await createCaseFromLoanDisk(row.loan_application_id, "upcoming");
      toast.success(`Added ${r.case.customer_name} as capital at risk (remaining principal £${(r.case.remaining_principal || 0).toLocaleString()})`);
      onCaseAdded();
    } catch (e) {
      toast.error("Add failed: " + errorDetailMessage(e, e.message));
    } finally { setBusyId(null); }
  };

  return (
    <Card className="border-amber-200 bg-amber-50/30" data-testid="bd-loandisk-search-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Search className="w-4 h-4 text-amber-700" />
          Add an Upcoming Capital-at-Risk case from LoanDisk
        </CardTitle>
        <p className="text-xs text-muted-foreground">Search live LoanDisk loans (any status) and flag them as at-risk before they go legal.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <form onSubmit={runSearch} className="flex gap-2">
          <Input
            placeholder="Type customer or business name (≥ 2 chars)…"
            value={q}
            onChange={e => setQ(e.target.value)}
            data-testid="bd-loandisk-search-input"
          />
          <Button type="submit" size="sm" disabled={searching} data-testid="bd-loandisk-search-btn">
            {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
            <span className="ml-1.5">Search</span>
          </Button>
        </form>

        {results.length > 0 && (
          <div className="overflow-x-auto bg-white rounded-md border">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 text-muted-foreground uppercase text-[10px]">
                <tr>
                  <th className="text-left px-2 py-1.5 font-semibold">Borrower</th>
                  <th className="text-left px-2 py-1.5 font-semibold">SF#</th>
                  <th className="text-left px-2 py-1.5 font-semibold">Status</th>
                  <th className="text-right px-2 py-1.5 font-semibold">Principal</th>
                  <th className="text-right px-2 py-1.5 font-semibold">Balance</th>
                  <th className="text-right px-2 py-1.5 font-semibold">DPD</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {results.map(r => {
                  // A loan is "live" (can be flagged as Capital at Risk) when
                  // it's still on the book — Active (1) or Restructured (4).
                  // Closed (2) shouldn't be flagged; Legal (3) already syncs.
                  const isLive = [1, 4].includes(r.loan_status_id);
                  return (
                    <tr key={r.loan_id} className="border-t hover:bg-amber-50/50">
                      <td className="px-2 py-1.5 font-medium">{r.borrower_name}</td>
                      <td className="px-2 py-1.5 text-muted-foreground">{r.loan_application_id}</td>
                      <td className="px-2 py-1.5">
                        <Badge variant="outline" className={
                          r.loan_status_id === 3 ? "bg-red-50 text-red-700 border-red-300" :
                          r.loan_status_id === 1 ? "bg-emerald-50 text-emerald-700 border-emerald-300" :
                          r.loan_status_id === 4 ? "bg-purple-50 text-purple-700 border-purple-300" :
                          "bg-slate-50 text-slate-700"
                        }>{r.loan_status_label}</Badge>
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmtGBP(r.principal_amount)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmtGBP(r.balance_amount)}</td>
                      <td className={`px-2 py-1.5 text-right tabular-nums ${r.days_past_due > 90 ? "text-red-700 font-semibold" : ""}`}>{r.days_past_due}</td>
                      <td className="px-2 py-1.5 text-right">
                        <Button
                          size="sm" variant="outline"
                          disabled={busyId === r.loan_id || !isLive}
                          onClick={() => addAsUpcoming(r)}
                          className="text-[11px] h-7"
                          data-testid={`bd-add-upcoming-${r.loan_id}`}
                          title={
                            isLive ? "Add as upcoming capital at risk" :
                            r.loan_status_id === 3 ? "Already in Legal — picked up by sync" :
                            "Closed loans can't be flagged"
                          }
                        >
                          {busyId === r.loan_id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Add as upcoming"}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Cases matrix — customer rows × month columns, like the original spreadsheet.
// Each cell shows the write-off £ for that customer in that month (most
// customers only appear in ONE month — the month they went legal).
// Sticky left columns (customer / SF# / default % / status / total) keep the
// key info on-screen while scrolling through 24+ months.
// ---------------------------------------------------------------------------
function CasesMatrix({ rows, monthsAxis, onSelect, emptyText, testIdPrefix, tone, autoScrollMonth }) {
  const scrollRef = useRef(null);

  // Sticky left-column widths (kept in one place so head/body/foot align AND
  // so the auto-scroll calculation knows where the scrollable region starts).
  const LEFT = {
    customer: { left: 0,    width: 220 },
    sf:       { left: 220,  width: 85  },
    pct:      { left: 305,  width: 60  },
    status:   { left: 365,  width: 90  },
    total:    { left: 455,  width: 105 },
  };
  const STICKY_WIDTH = LEFT.total.left + LEFT.total.width; // 560px
  const MONTH_COL_WIDTH = 78; // matches min-w-[78px] on month <th>

  // Auto-scroll: when the matrix mounts (or when monthsAxis / rows change due
  // to data loading or period filter), scroll the container so the requested
  // month is roughly 1/3 of the way into the visible scroll area — enough
  // context to the left (recent history) without burying the focus month.
  useEffect(() => {
    if (!autoScrollMonth || !scrollRef.current || monthsAxis.length === 0) return;
    const idx = monthsAxis.indexOf(autoScrollMonth);
    if (idx === -1) return; // requested month not in axis (e.g. filtered out)
    const container = scrollRef.current;
    const targetX = idx * MONTH_COL_WIDTH; // distance from start of month columns
    // Offset so target sits ~1/3 into the scrollable region (after sticky cols)
    const offset = Math.max(0, container.clientWidth - STICKY_WIDTH) / 3;
    container.scrollLeft = Math.max(0, targetX - offset);
  }, [autoScrollMonth, monthsAxis, rows.length, STICKY_WIDTH]);

  if (rows.length === 0) {
    return <Card><CardContent className="py-12 text-center text-muted-foreground text-sm">{emptyText}</CardContent></Card>;
  }

  const headTone = tone === "amber" ? "bg-amber-50 text-amber-900" : "bg-slate-50";
  const upcoming = tone === "amber";

  // Per-month column total (foot row). For upcoming-capital-at-risk the
  // per-row column shows `remaining_principal` (exposure) — not
  // `write_off_amount` (which is 0 because default_pct=0 for upcoming).
  // The totals row must use the same field so the bottom sum reconciles
  // with what ops sees in each cell.
  const monthTotals = monthsAxis.map(m =>
    rows.reduce((sum, r) => {
      if (r.write_off_month !== m) return sum;
      const amt = upcoming ? (r.remaining_principal || 0) : (r.write_off_amount || 0);
      return sum + amt;
    }, 0)
  );
  const grandTotal = monthTotals.reduce((a, b) => a + b, 0);

  // The sticky left columns and the right-most sticky column need an opaque
  // background that matches the row they belong to (thead = slate-50,
  // body = white, hover = slate-50, tfoot = slate-100). `bg-inherit` is
  // unreliable on sticky <th>/<td> elements — Safari and some Chromium builds
  // render it as transparent when the scroll container moves, which lets the
  // scrolling month columns visually bleed through the sticky left header
  // labels. We use explicit Tailwind bg-* classes instead, and a strong
  // box-shadow on the right-most sticky column so the scroll boundary reads
  // clearly when content slides underneath.
  const STICKY_HEAD_BG = tone === "amber" ? "bg-amber-50" : "bg-slate-50";
  const STICKY_LAST_SHADOW = "shadow-[6px_0_8px_-4px_rgba(15,23,42,0.18)]";

  return (
    <Card>
      <CardContent className="p-0">
        {/* Fixed-height scroll viewport — both axes scroll INSIDE the card so
            the horizontal scrollbar sits at a predictable spot (bottom of the
            card, not the bottom of the page) and the column headers stay
            visible on vertical scroll. */}
        <div
          ref={scrollRef}
          className="relative overflow-auto max-w-full"
          style={{ maxHeight: "min(70vh, 720px)" }}
          data-testid={`${testIdPrefix}-matrix`}
        >
          <table className="text-xs border-collapse" style={{ minWidth: "1400px", width: "max-content" }}>
            <thead className={`${headTone} text-[10px] uppercase sticky top-0 z-30 shadow-[0_1px_0_0_#cbd5e1]`}>
              <tr>
                <th className={`sticky left-0 z-40 text-left px-3 py-2 font-semibold border-r ${STICKY_HEAD_BG}`} style={{ left: LEFT.customer.left, minWidth: LEFT.customer.width }}>Customer</th>
                <th className={`sticky z-40 text-left px-2 py-2 font-semibold border-r ${STICKY_HEAD_BG}`} style={{ left: LEFT.sf.left, minWidth: LEFT.sf.width }}>SF#</th>
                <th className={`sticky z-40 text-right px-2 py-2 font-semibold border-r ${STICKY_HEAD_BG}`} style={{ left: LEFT.pct.left, minWidth: LEFT.pct.width }}>Def %</th>
                <th className={`sticky z-40 text-left px-2 py-2 font-semibold border-r ${STICKY_HEAD_BG}`} style={{ left: LEFT.status.left, minWidth: LEFT.status.width }}>Status</th>
                <th className={`sticky z-40 text-right px-2 py-2 font-semibold border-r-2 border-slate-300 ${STICKY_HEAD_BG} ${STICKY_LAST_SHADOW}`} style={{ left: LEFT.total.left, minWidth: LEFT.total.width }}>{upcoming ? "Exposure" : "Write-off"}</th>
                {monthsAxis.map(m => (
                  <th key={m} className="text-right px-1.5 py-2 font-semibold whitespace-nowrap min-w-[78px]">
                    {fmtMonth(m)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr
                  key={r.case_id}
                  className="border-t hover:bg-slate-50 cursor-pointer group"
                  onClick={() => onSelect(r)}
                  data-testid={`${testIdPrefix}-row-${r.case_id}`}
                >
                  <td className="sticky left-0 z-20 px-3 py-1.5 font-medium bg-white group-hover:bg-slate-50 border-r truncate" style={{ left: LEFT.customer.left, maxWidth: LEFT.customer.width }} title={r.customer_name}>
                    {r.customer_name}
                  </td>
                  <td className="sticky z-20 px-2 py-1.5 text-muted-foreground bg-white group-hover:bg-slate-50 border-r" style={{ left: LEFT.sf.left }}>
                    {r.sfa_number || "—"}
                  </td>
                  <td className="sticky z-20 px-2 py-1.5 text-right tabular-nums bg-white group-hover:bg-slate-50 border-r" style={{ left: LEFT.pct.left }}>
                    {r.default_pct != null ? `${Math.round(r.default_pct * 100)}%` : "—"}
                  </td>
                  <td className="sticky z-20 px-2 py-1.5 bg-white group-hover:bg-slate-50 border-r" style={{ left: LEFT.status.left }}>
                    <Badge variant="outline" className={`${statusTone(r.status)} text-[10px] px-1`}>{statusLabel(r.status)}</Badge>
                  </td>
                  <td className={`sticky z-20 px-2 py-1.5 text-right tabular-nums bg-white group-hover:bg-slate-50 border-r-2 border-slate-300 font-semibold ${STICKY_LAST_SHADOW} ${upcoming ? "text-amber-700" : "text-red-700"}`} style={{ left: LEFT.total.left }}>
                    {upcoming ? fmtGBPdp(r.remaining_principal) : fmtGBPdp(r.write_off_amount)}
                  </td>
                  {monthsAxis.map(m => {
                    const isMine = r.write_off_month === m;
                    const amt = isMine ? (upcoming ? r.remaining_principal : r.write_off_amount) : 0;
                    return (
                      <td key={m} className={`px-1.5 py-1.5 text-right tabular-nums ${isMine ? (upcoming ? "bg-amber-50 text-amber-700 font-medium" : "bg-red-50 text-red-700 font-medium") : "text-slate-300"}`}>
                        {isMine ? fmtGBP(amt) : "·"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-100 font-semibold border-t-2 border-slate-300 sticky bottom-0 z-30">
              <tr>
                <td className="sticky left-0 z-40 px-3 py-2 bg-slate-100 border-r" style={{ left: LEFT.customer.left }}>Totals</td>
                <td className="sticky z-40 bg-slate-100 border-r" style={{ left: LEFT.sf.left }} />
                <td className="sticky z-40 bg-slate-100 border-r" style={{ left: LEFT.pct.left }} />
                <td className="sticky z-40 bg-slate-100 border-r" style={{ left: LEFT.status.left }} />
                <td className={`sticky z-40 px-2 py-2 text-right tabular-nums bg-slate-100 border-r-2 border-slate-300 ${STICKY_LAST_SHADOW} ${upcoming ? "text-amber-700" : "text-red-700"}`} style={{ left: LEFT.total.left }}>
                  {fmtGBP(grandTotal)}
                </td>
                {monthTotals.map((t, i) => (
                  <td key={i} className={`px-1.5 py-2 text-right tabular-nums ${t > 0 ? (upcoming ? "text-amber-700" : "text-red-700") : "text-slate-300"}`}>
                    {t > 0 ? fmtGBP(t) : "—"}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
        {/* Helper hint — clarifies horizontal scroll affordance and counts. */}
        <div className="px-3 py-1.5 text-[10px] text-muted-foreground border-t bg-slate-50/50 flex items-center justify-between">
          <span>{rows.length} {upcoming ? "upcoming-at-risk" : "active"} cases · scroll horizontally for all {monthsAxis.length} months</span>
          <span className="font-mono">↔ shift+scroll to pan</span>
        </div>
      </CardContent>
    </Card>
  );
}

// Status options shown in the case-edit dropdowns. Includes both portal-
// internal lifecycle states (upcoming/legal/recovered/settled) and ops
// write-off labels (write_off / partial_settlement / agreed_pp). Ordered
// roughly by pipeline progression.
const BD_STATUS_OPTIONS = [
  { value: "upcoming",            label: "Upcoming (capital at risk)" },
  { value: "legal",               label: "Legal" },
  { value: "agreed_payment_plan", label: "Agreed Payment Plan" },
  { value: "partial_settlement",  label: "Partial Settlement" },
  { value: "settled",             label: "Settled (F&F)" },
  { value: "write_off",           label: "Write-Off" },
  { value: "recovered",           label: "Recovered" },
  { value: "restructured",        label: "Restructured" },
  { value: "closed",              label: "Closed" },
];

function statusTone(status) {
  switch ((status || "").toLowerCase()) {
    case "legal":               return "bg-red-50 text-red-700 border-red-200";
    case "write_off":           return "bg-red-100 text-red-800 border-red-300";
    case "upcoming":            return "bg-amber-50 text-amber-700 border-amber-200";
    case "agreed_payment_plan": return "bg-yellow-50 text-yellow-700 border-yellow-200";
    case "partial_settlement":  return "bg-blue-50 text-blue-700 border-blue-200";
    case "settled":             return "bg-blue-50 text-blue-700 border-blue-200";
    case "recovered":           return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "restructured":        return "bg-purple-50 text-purple-700 border-purple-200";
    case "closed":              return "bg-slate-50 text-slate-700 border-slate-200";
    default:                    return "bg-slate-50 text-slate-700 border-slate-200";
  }
}

function statusLabel(value) {
  return BD_STATUS_OPTIONS.find(o => o.value === value)?.label || value || "—";
}

// ---------------------------------------------------------------------------
// OutcomeSelector — 2×2 grid of colour-coded outcome cards + a partial-write-
// off chip row that only appears when "Written Off" is the active outcome.
// Pure UI; emits the chosen outcome key + (for written_off) the partial pct.
// ---------------------------------------------------------------------------
function OutcomeSelector({ value, defaultPct, onChange }) {
  // Partial write-off chips from real data distribution (xlsx): 50/70/80/90/100.
  const PARTIAL_OPTIONS = [0.5, 0.7, 0.8, 0.9, 1.0];
  const showPartial = value === "written_off";

  const toneClasses = (tone, selected) => {
    const map = {
      red:     selected ? "bg-red-50 border-red-400 ring-2 ring-red-200"         : "bg-white border-slate-200 hover:border-red-200 hover:bg-red-50/40",
      amber:   selected ? "bg-amber-50 border-amber-400 ring-2 ring-amber-200"   : "bg-white border-slate-200 hover:border-amber-200 hover:bg-amber-50/40",
      blue:    selected ? "bg-blue-50 border-blue-400 ring-2 ring-blue-200"      : "bg-white border-slate-200 hover:border-blue-200 hover:bg-blue-50/40",
      emerald: selected ? "bg-emerald-50 border-emerald-400 ring-2 ring-emerald-200" : "bg-white border-slate-200 hover:border-emerald-200 hover:bg-emerald-50/40",
    };
    return map[tone] || map.amber;
  };
  const iconToneClasses = (tone, selected) => {
    const map = {
      red:     selected ? "text-red-700"     : "text-red-500/70",
      amber:   selected ? "text-amber-700"   : "text-amber-500/70",
      blue:    selected ? "text-blue-700"    : "text-blue-500/70",
      emerald: selected ? "text-emerald-700" : "text-emerald-500/70",
    };
    return map[tone] || map.amber;
  };

  return (
    <div className="space-y-3" data-testid="bd-outcome-selector">
      <div className="grid grid-cols-3 gap-2">
        {OUTCOMES.map(o => {
          const selected = o.key === value;
          const Icon = o.icon;
          return (
            <button
              key={o.key}
              type="button"
              onClick={() => onChange(o.key, o.supportsPartial ? (defaultPct > 0 ? defaultPct : 1) : null)}
              className={`text-left p-3 rounded-md border-2 transition-all ${toneClasses(o.tone, selected)}`}
              data-testid={`bd-outcome-${o.key}`}
            >
              <div className="flex items-start gap-2">
                <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${iconToneClasses(o.tone, selected)}`} />
                <div className="min-w-0">
                  <div className="text-sm font-semibold">{o.label}</div>
                  <div className="text-[11px] text-muted-foreground leading-tight">{o.sublabel}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      {showPartial && (
        <div className="rounded-md border bg-red-50/40 border-red-200 px-3 py-2.5 space-y-2" data-testid="bd-partial-section">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-semibold text-red-900">Write-off %</Label>
            <span className="text-[10px] text-muted-foreground">
              {defaultPct >= 1 ? "Full loss" : `Settled at ${Math.round((1 - defaultPct) * 100)}% · writing off ${Math.round(defaultPct * 100)}%`}
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {PARTIAL_OPTIONS.map(p => {
              const selected = Math.abs((defaultPct || 0) - p) < 0.001;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => onChange("written_off", p)}
                  className={`h-8 px-3 rounded-md text-xs font-medium border transition-colors ${
                    selected
                      ? "bg-red-600 text-white border-red-600"
                      : "bg-white border-slate-200 hover:bg-red-50 hover:border-red-200"
                  }`}
                  data-testid={`bd-partial-${Math.round(p * 100)}`}
                >
                  {Math.round(p * 100)}%
                </button>
              );
            })}
            <div className="flex items-center gap-1.5 ml-1">
              <Label className="text-[10px] text-muted-foreground">Custom</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={defaultPct}
                onChange={(e) => onChange("written_off", parseFloat(e.target.value) || 0)}
                className="h-8 w-[80px] text-xs"
                data-testid="bd-partial-custom"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Outcome model — the user's mental model of what can happen to a bad-debt
// case in real life. Replaces the (status × default_pct × category) triplet
// in the UI with 4 discrete buckets. Every case ends up in one of these:
//
//   • active_bad_debt — still being pursued legally, outcome TBD
//   • written_off    — permanent loss (full 100%, or partial F&F settlement)
//   • restructured   — paid out / restructured back to live collections, 0%
//   • recovered      — paid in full, 0% loss, closed
//
// The data in the ops xlsx confirms this is exhaustive: 126 cases broke down
// as 82% full write-off (default_pct=1), 11% restructured/recovered (=0),
// and the rest were partial settlements at 50/70/80/90%.
// ---------------------------------------------------------------------------
const OUTCOMES = [
  {
    key: "active_bad_debt",
    label: "Active in Bad Debt",
    sublabel: "Still pursuing · outcome TBD",
    tone: "amber",
    icon: AlertTriangle,
    status: "legal",
    default_pct: 1.0,
    category: "active",
  },
  {
    key: "written_off",
    label: "Written Off",
    sublabel: "Permanent loss · locked",
    tone: "red",
    icon: TrendingDown,
    status: "write_off",
    default_pct: 1.0,
    category: "active",
    supportsPartial: true,
  },
  {
    key: "restructured",
    label: "Restructured",
    sublabel: "Back to live collections → Upcoming at Risk",
    tone: "blue",
    icon: RefreshCw,
    status: "restructured",
    default_pct: 0.0,
    category: "upcoming",
  },
];

// Map (status, default_pct, category) → outcome key. Falls back to
// "active_bad_debt" so newly-synced legal cases land sensibly. "Recovered"
// is no longer a manual outcome — when a loan is fully recovered it just
// drops off LoanDisk's Legal status and the daily sync removes it.
function deriveOutcome(status, defaultPct, category) {
  const s = (status || "").toLowerCase();
  if (s === "restructured") return "restructured";
  if (s === "write_off") return "written_off";
  if (s === "partial_settlement" || s === "settled") return "written_off";
  // legal / agreed_payment_plan / upcoming / recovered etc.
  if (category === "upcoming" && (defaultPct ?? 0) === 0) return "restructured";
  return "active_bad_debt";
}

// ---------------------------------------------------------------------------
// Borrower loan history — mirrors LoanDisk's "Borrower Loans Statement"
// view. Collapsible card with the full loan list + totals row for the
// borrower attached to the case. Lazy-loaded so opening the drawer for a
// routine edit doesn't trigger a LoanDisk roundtrip every time.
// ---------------------------------------------------------------------------
function BorrowerLoanHistory({ sfaNumber, caseLoanId, flat = false }) {
  // `flat` strips the outer Card wrapper / collapsible behaviour — used
  // when this component is embedded inside another Card (e.g. the case
  // drawer's "Financial Information" section) so we don't get nested
  // borders and double-padding.
  const [open, setOpen] = useState(flat);  // when flat, always open
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(async (forceFresh = false) => {
    if (!sfaNumber) return;
    try {
      setLoading(true);
      setError(null);
      const r = await getBorrowerLoans(sfaNumber, forceFresh);
      setData(r);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [sfaNumber]);

  // Auto-fetch on mount when in flat mode (the section is always visible
  // so we should pre-populate without waiting for a user click).
  useEffect(() => {
    if (flat && !data && !loading) fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flat, sfaNumber]);

  const toggleOpen = () => {
    if (flat) return; // can't collapse in flat mode
    const next = !open;
    setOpen(next);
    if (next && !data && !loading) fetchHistory();
  };

  // The inner content — table + states. Reused by both flat and Card modes.
  const innerContent = (
    <>
      {loading && (
        <div className="text-center py-6 text-xs text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
          Fetching from LoanDisk…
        </div>
      )}
      {error && (
        <div className="px-4 py-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded">
          {error}
        </div>
      )}
      {data && data.loans && data.loans.length > 0 && (
        <div className={`overflow-x-auto ${flat ? "border rounded-md" : "border-t"}`} data-testid="bd-borrower-history-table">
          <table className="text-[11px] w-full">
            <thead className="bg-slate-50 text-[10px] uppercase text-muted-foreground">
              <tr>
                <th className="text-left px-2 py-2 font-semibold">Released</th>
                <th className="text-left px-2 py-2 font-semibold">Loan#</th>
                <th className="text-right px-2 py-2 font-semibold">Principal</th>
                <th className="text-right px-2 py-2 font-semibold">Rate</th>
                <th className="text-right px-2 py-2 font-semibold">Total Due</th>
                <th className="text-right px-2 py-2 font-semibold">Paid</th>
                <th className="text-right px-2 py-2 font-semibold">Balance</th>
                <th className="text-left px-2 py-2 font-semibold">Last Pmt</th>
                <th className="text-left px-2 py-2 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.loans.map(ln => {
                const isCurrentCase = caseLoanId && String(caseLoanId) === String(ln.loan_id);
                return (
                  <tr
                    key={ln.loan_id}
                    className={`border-t hover:bg-slate-50 ${isCurrentCase ? "bg-red-50/40" : ""}`}
                    data-testid={`bd-borrower-history-row-${ln.loan_id}`}
                  >
                    <td className="px-2 py-1.5 text-muted-foreground whitespace-nowrap">{ln.released_date || "—"}</td>
                    <td className="px-2 py-1.5 font-medium whitespace-nowrap">
                      {ln.loan_ref}
                      {isCurrentCase && <Badge variant="outline" className="ml-1.5 text-[9px] px-1 bg-red-100 text-red-800 border-red-300">this case</Badge>}
                      {ln.restructured_from && (
                        <div className="text-[10px] text-muted-foreground">R: {ln.restructured_from}</div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmtGBPdp(ln.principal_amount)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">
                      {ln.interest_rate != null ? `${ln.interest_rate}%` : "—"}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmtGBPdp(ln.total_due)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmtGBPdp(ln.total_paid)}</td>
                    <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${ln.balance > 0 ? "text-red-700" : "text-emerald-700"}`}>
                      {fmtGBPdp(ln.balance)}
                    </td>
                    <td className="px-2 py-1.5 text-muted-foreground whitespace-nowrap text-[10px]">
                      {ln.last_payment_date || "—"}
                      {ln.last_payment_amount > 0 && <div>{fmtGBPdp(ln.last_payment_amount)}</div>}
                    </td>
                    <td className="px-2 py-1.5">
                      <Badge variant="outline" className={`${statusTone(ln.loan_status_label)} text-[9px] px-1.5`}>{ln.loan_status_label}</Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-slate-50 font-semibold border-t-2 border-slate-300 text-[11px]">
              <tr>
                <td className="px-2 py-2" colSpan={2}>Totals</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmtGBPdp(data.totals?.principal_amount || 0)}</td>
                <td />
                <td className="px-2 py-2 text-right tabular-nums">{fmtGBPdp(data.totals?.total_due || 0)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmtGBPdp(data.totals?.total_paid || 0)}</td>
                <td className={`px-2 py-2 text-right tabular-nums ${(data.totals?.balance || 0) > 0 ? "text-red-700" : "text-emerald-700"}`}>
                  {fmtGBPdp(data.totals?.balance || 0)}
                </td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      {data && data.loans && data.loans.length === 0 && (
        <div className="text-center py-6 text-xs text-muted-foreground">
          No loans found for this borrower in LoanDisk.
        </div>
      )}
      {!data && !loading && !error && !flat && (
        <div className="text-center py-6 text-xs text-muted-foreground">
          Click to load this borrower's loan history from LoanDisk.
        </div>
      )}
    </>
  );

  // Flat mode — no outer Card wrapper. Renders just a small header strip
  // (borrower name, count, totals, cached/live badge, refresh button) and
  // the table below it. Used when embedded inside another Card.
  if (flat) {
    return (
      <div className="space-y-2" data-testid="bd-borrower-history-flat">
        <div className="flex items-center justify-between gap-2 text-xs">
          <div className="text-muted-foreground">
            {data ? (
              <>
                <span className="font-semibold text-foreground">Borrower history:</span> {data.borrower_name} · {data.count} loans · {fmtGBPdp(data.totals?.balance || 0)} outstanding
                {data.cache_hit === false && <span className="ml-1 text-emerald-700">· live</span>}
                {data.cache_hit === true && <span className="ml-1 text-blue-700" title={`Cached at ${data.cached_at}`}>· cached</span>}
              </>
            ) : (
              <span className="font-semibold text-foreground">Borrower history</span>
            )}
          </div>
          <Button size="sm" variant="ghost" onClick={() => fetchHistory(true)} disabled={loading} className="h-7 px-2 text-[11px]" data-testid="bd-borrower-history-refresh" title="Force a fresh pull from LoanDisk (bypasses 24h cache)">
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          </Button>
        </div>
        {innerContent}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3 cursor-pointer select-none" onClick={toggleOpen} data-testid="bd-borrower-history-header">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4" />
            Borrower Loan History
            {data && (
              <span className="text-xs font-normal text-muted-foreground ml-2">
                {data.borrower_name} · {data.count} loans · {fmtGBPdp(data.totals?.balance || 0)} outstanding
                {data.cache_hit === false && <span className="ml-1 text-emerald-700">· live</span>}
                {data.cache_hit === true && <span className="ml-1 text-blue-700" title={`Cached at ${data.cached_at}`}>· cached</span>}
              </span>
            )}
          </CardTitle>
          <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
            {open && (
              <Button size="sm" variant="ghost" onClick={() => fetchHistory(true)} disabled={loading} className="h-7 px-2 text-[11px]" data-testid="bd-borrower-history-refresh" title="Force a fresh pull from LoanDisk (bypasses 24h cache)">
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              </Button>
            )}
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </div>
        </div>
      </CardHeader>
      {open && <CardContent className="p-0">{innerContent}</CardContent>}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// EditLoanDialog — opened from Case Details when ops needs to re-link the
// case to a different LoanDisk loan (e.g. the auto-pull picked the wrong
// SF# for a customer with multiple loans). Mirrors the LoanDiskSearchPanel
// search UX but, on row-click, calls the relink endpoint instead of
// creating a new case.
// ---------------------------------------------------------------------------
function EditLoanDialog({ open, onOpenChange, caseId, currentRef, currentCustomer, onRelinked }) {
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [resultsMode, setResultsMode] = useState("borrower"); // "borrower" | "search"
  const [busyId, setBusyId] = useState(null);

  // On open, auto-load this borrower's full LoanDisk loan list from the 24h
  // cache. Saves ops typing a search just to see "same borrower, different
  // SF#" — which is the most common reason to re-link a case in the first
  // place. The manual search input still works as a fallback for re-linking
  // to a completely different borrower (rare but possible).
  useEffect(() => {
    if (!open || !currentRef) return;
    let cancelled = false;
    (async () => {
      try {
        setSearching(true);
        const r = await getBorrowerLoans(currentRef);
        if (cancelled) return;
        // Shape borrower-loans rows into the same fields search results use
        // so a single render path covers both modes.
        const shaped = (r.loans || []).map(ln => ({
          loan_id: ln.loan_id,
          loan_application_id: ln.loan_ref,
          borrower_name: r.borrower_name,
          loan_status_label: ln.loan_status_label,
          principal_amount: ln.principal_amount,
          balance_amount: ln.balance,
          released_date: ln.released_date,  // shown in the table
        }));
        setResults(shaped);
        setResultsMode("borrower");
      } catch (err) {
        if (!cancelled) toast.error("Couldn't load borrower's loans: " + errorDetailMessage(err, err.message));
      } finally {
        if (!cancelled) setSearching(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, currentRef]);

  const runSearch = async (e) => {
    e?.preventDefault?.();
    if (!q.trim() || q.trim().length < 2) {
      toast.error("Type at least 2 characters");
      return;
    }
    try {
      setSearching(true);
      const r = await searchLoanDisk(q.trim());
      setResults(r.results || []);
      setResultsMode("search");
      if ((r.results || []).length === 0) toast.info("No LoanDisk matches");
    } catch (err) {
      toast.error("LoanDisk search failed: " + errorDetailMessage(err, err.message));
    } finally {
      setSearching(false);
    }
  };

  const pickLoan = async (row) => {
    if (!confirm(`Re-link this case to ${row.loan_application_id} (${row.borrower_name})? The case will be re-computed against the new loan's chain.`)) return;
    try {
      setBusyId(row.loan_id);
      const r = await relinkCaseToLoanDisk(caseId, row.loan_application_id);
      toast.success(`Re-linked to ${r.case.sfa_number} (${r.case.customer_name})`);
      onRelinked(r.case);
      onOpenChange(false);
    } catch (err) {
      toast.error("Re-link failed: " + errorDetailMessage(err, err.message));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Re-link case to a different LoanDisk loan</DialogTitle>
          <DialogDescription>
            Currently linked to <span className="font-mono">{currentRef}</span>. {resultsMode === "borrower"
              ? `Showing all of ${currentCustomer || "this borrower"}'s loans below — pick one to re-link.`
              : `Search results — pick one to re-link.`}
            {" "}Notes, outcome, and payment log are preserved.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={runSearch} className="flex gap-2">
          <Input
            placeholder="Or search LoanDisk by SF#, borrower name… (leave empty to see this borrower's loans)"
            value={q}
            onChange={e => setQ(e.target.value)}
            data-testid="bd-edit-loan-search-input"
          />
          <Button type="submit" size="sm" disabled={searching} data-testid="bd-edit-loan-search-btn">
            {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
            <span className="ml-1.5">Search</span>
          </Button>
        </form>
        {searching && results.length === 0 && (
          <div className="text-center py-8 text-xs text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
            Loading…
          </div>
        )}
        {!searching && results.length === 0 && (
          <div className="text-center py-8 text-xs text-muted-foreground">
            No results. Try searching LoanDisk above.
          </div>
        )}
        {results.length > 0 && (
          <>
            <div className="text-[11px] text-muted-foreground -mb-1">
              {resultsMode === "borrower"
                ? <>Showing all loans for <span className="font-semibold text-foreground">{currentCustomer}</span> from the 24h cache · {results.length} loan{results.length === 1 ? "" : "s"}</>
                : <>Search results for <span className="font-semibold text-foreground">"{q}"</span> · {results.length} match{results.length === 1 ? "" : "es"}</>
              }
            </div>
            <div className="overflow-x-auto max-h-[440px] border rounded-md">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 text-muted-foreground uppercase text-[10px] sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1.5 font-semibold">Borrower</th>
                    <th className="text-left px-2 py-1.5 font-semibold">SF#</th>
                    <th className="text-left px-2 py-1.5 font-semibold">Released</th>
                    <th className="text-left px-2 py-1.5 font-semibold">Status</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Principal</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Balance</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {results.map(row => {
                    const isCurrent = row.loan_application_id === currentRef;
                    return (
                      <tr key={row.loan_id} className={`border-t hover:bg-slate-50 ${isCurrent ? "bg-blue-50/40" : ""}`}>
                        <td className="px-2 py-1.5">{row.borrower_name}</td>
                        <td className="px-2 py-1.5 font-mono">
                          {row.loan_application_id}
                          {isCurrent && <Badge variant="outline" className="ml-1.5 text-[9px] bg-blue-100 text-blue-800 border-blue-300">current</Badge>}
                        </td>
                        <td className="px-2 py-1.5 text-muted-foreground whitespace-nowrap text-[10px]">{row.released_date || "—"}</td>
                        <td className="px-2 py-1.5"><Badge variant="outline" className="text-[9px]">{row.loan_status_label}</Badge></td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{fmtGBPdp(row.principal_amount || 0)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{fmtGBPdp(row.balance_amount || 0)}</td>
                        <td className="px-2 py-1.5 text-right">
                          <Button
                            size="sm"
                            variant={isCurrent ? "ghost" : "outline"}
                            disabled={busyId === row.loan_id || isCurrent}
                            onClick={() => pickLoan(row)}
                            className="h-7 px-2 text-[11px]"
                            data-testid={`bd-edit-loan-pick-${row.loan_application_id}`}
                          >
                            {busyId === row.loan_id ? <Loader2 className="w-3 h-3 animate-spin" /> : isCurrent ? "Linked" : "Re-link"}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
function CaseDrawer({ caseRow, open, onClose, onChanged }) {
  const [edit, setEdit] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [payment, setPayment] = useState({ date: "", amount: "", source: "without_prejudice", notes: "" });
  const [saving, setSaving] = useState(false);
  const [detailsTab, setDetailsTab] = useState("details");
  const [editLoanOpen, setEditLoanOpen] = useState(false);

  useEffect(() => {
    if (caseRow) {
      setEdit({
        customer_name: caseRow.customer_name || "",
        sfa_number: caseRow.sfa_number || "",
        original_principal: caseRow.original_principal ?? "",
        remaining_principal: caseRow.remaining_principal ?? "",
        default_pct: caseRow.default_pct ?? 1,
        status: caseRow.status || "legal",
        category: caseRow.category || "active",
        write_off_month: caseRow.write_off_month || "",
        notes: caseRow.notes || "",
      });
      setPreview(null);
      setPayment({ date: new Date().toISOString().slice(0, 10), amount: "", source: "without_prejudice", notes: "" });
    }
  }, [caseRow]);

  if (!caseRow || !edit) return null;

  const runPreview = async () => {
    if (!edit.sfa_number) {
      toast.error("Enter SF# first");
      return;
    }
    try {
      setPreviewLoading(true);
      const r = await getBadDebtPrincipalPreview(edit.sfa_number);
      setPreview(r);
      if (r.ok && r.remaining_principal != null) {
        toast.success(`Calculated: ${fmtGBPdp(r.remaining_principal)} remaining principal`);
      } else {
        toast.error(r.error || "Calculation failed");
      }
    } catch (e) {
      toast.error("Preview failed: " + errorDetailMessage(e, e.message));
    } finally {
      setPreviewLoading(false);
    }
  };

  const applyPreview = () => {
    if (!preview?.ok) return;
    setEdit(prev => ({
      ...prev,
      original_principal: preview.original_principal,
      remaining_principal: preview.remaining_principal,
    }));
    toast.success("Preview values copied into case");
  };

  // One-click variant: fetch the LoanDisk chain calc AND apply it in a single
  // step. Used from the Financials card so ops don't have to expand the
  // advanced LoanDisk Principal Calculator just to refresh stale balances.
  const refreshPrincipal = async () => {
    if (!edit.sfa_number) {
      toast.error("Enter SF# first");
      return;
    }
    try {
      setPreviewLoading(true);
      const r = await getBadDebtPrincipalPreview(edit.sfa_number);
      setPreview(r);
      if (r.ok && r.remaining_principal != null) {
        setEdit(prev => ({
          ...prev,
          original_principal: r.original_principal,
          remaining_principal: r.remaining_principal,
        }));
        toast.success(`Refreshed from LoanDisk: ${fmtGBPdp(r.remaining_principal)}`);
      } else {
        toast.error(r.error || "LoanDisk refresh failed");
      }
    } catch (e) {
      toast.error("Refresh failed: " + errorDetailMessage(e, e.message));
    } finally {
      setPreviewLoading(false);
    }
  };

  const save = async () => {
    try {
      setSaving(true);
      const payload = {
        ...edit,
        default_pct: parseFloat(edit.default_pct) || 0,
        original_principal: edit.original_principal === "" ? null : parseFloat(edit.original_principal),
        remaining_principal: edit.remaining_principal === "" ? null : parseFloat(edit.remaining_principal),
      };
      await updateBadDebtCase(caseRow.case_id, payload);
      toast.success("Case updated");
      onChanged();
    } catch (e) {
      toast.error("Save failed: " + errorDetailMessage(e, e.message));
    } finally {
      setSaving(false);
    }
  };

  const logPayment = async () => {
    if (!payment.amount || parseFloat(payment.amount) <= 0) {
      toast.error("Enter a positive amount");
      return;
    }
    try {
      setSaving(true);
      const r = await logBadDebtPayment(caseRow.case_id, {
        date: payment.date,
        amount: parseFloat(payment.amount),
        source: payment.source,
        notes: payment.notes,
      });
      toast.success(`Payment logged · new remaining: ${fmtGBPdp(r.new_remaining_principal)}`);
      onChanged();
    } catch (e) {
      toast.error("Log payment failed: " + errorDetailMessage(e, e.message));
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!confirm(`Delete case "${caseRow.customer_name}"? This can't be undone.`)) return;
    try {
      await deleteBadDebtCase(caseRow.case_id);
      toast.success("Case deleted");
      onChanged();
    } catch (e) {
      toast.error("Delete failed: " + errorDetailMessage(e, e.message));
    }
  };

  // LoanDisk direct link (no auth — opens LoanDisk in a new tab so ops
  // can flip the status manually). LoanDisk loan URLs follow a stable
  // pattern via loan_id; here we use the loan_application_id route too.
  const ldLink = caseRow.loandisk_loan_id
    ? `https://app.loandisk.com/loan/view/${caseRow.loandisk_loan_id}`
    : null;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader className="pb-3 border-b">
          <SheetTitle className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="truncate text-lg" data-testid="bd-drawer-customer">{caseRow.customer_name}</div>
              <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground font-normal">
                <span data-testid="bd-drawer-sfa">{caseRow.sfa_number || "no SF#"}</span>
                {caseRow.write_off_month && <span>· {fmtMonth(caseRow.write_off_month)}</span>}
                {caseRow.imported_at && <span>· logged {caseRow.imported_at.slice(0, 10)}</span>}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className={`text-xl font-bold tabular-nums ${caseRow.category === "upcoming" ? "text-amber-700" : "text-red-700"}`} data-testid="bd-drawer-amount">
                {caseRow.category === "upcoming"
                  ? fmtGBPdp(caseRow.remaining_principal || 0)
                  : fmtGBPdp(caseRow.write_off_amount || 0)}
              </div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                {caseRow.category === "upcoming" ? "Capital at risk" : "Write-off £"}
              </div>
            </div>
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-5 mt-5">
          {/* Locked-state banner — when status is write_off, the case is
              permanently in bad debt. The Edit loan button still works as
              an escape hatch (e.g. wrong loan linked, need to fix), but
              all other edit controls are disabled to prevent accidental
              "un-write-off" of a closed case. */}
          {edit.status === "write_off" && (
            <div className="rounded-md border-2 border-red-300 bg-red-50 px-3 py-2.5 flex items-center gap-2 text-sm" data-testid="bd-written-off-banner">
              <TrendingDown className="w-4 h-4 text-red-700 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-red-900">This case is written off — locked</div>
                <div className="text-xs text-red-700">Write-off is permanent in bad debt. Use the <span className="font-semibold">Edit loan</span> button above to re-link or correct the underlying loan.</div>
              </div>
            </div>
          )}
          {edit.status === "partial_settlement" && (
            <div className="rounded-md border-2 border-red-300 bg-red-50 px-3 py-2.5 flex items-center gap-2 text-sm" data-testid="bd-written-off-banner">
              <TrendingDown className="w-4 h-4 text-red-700 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-red-900">Partial settlement — locked at {Math.round((parseFloat(edit.default_pct) || 0) * 100)}% write-off</div>
                <div className="text-xs text-red-700">Use the <span className="font-semibold">Edit loan</span> button above if you need to reopen or correct this case.</div>
              </div>
            </div>
          )}

          {/* Wrap the editable cards in a single element whose
              pointer-events / opacity flip when the case is locked.
              The Edit loan button is outside this wrapper so it stays
              clickable. */}
          <div className={(edit.status === "write_off" || edit.status === "partial_settlement") ? "pointer-events-none opacity-60 space-y-5" : "space-y-5"} data-testid="bd-drawer-editable-region">
          {/* ───────────── 1. CASE DETAILS ───────────── */}
          {/* Two tabs:
              - Details: identity fields (auto-pulled from LoanDisk, read-
                only). The "Edit loan" button opens a search dialog to
                re-link the case to a different LoanDisk loan.
              - Notes: full-bleed textarea for narrative + audit context. */}
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between gap-3">
              <CardTitle className="text-sm flex items-center gap-2"><Edit3 className="w-4 h-4" /> Case Details</CardTitle>
              {detailsTab === "details" && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditLoanOpen(true)}
                  className="h-7 px-2 text-[11px] pointer-events-auto"
                  data-testid="bd-edit-loan-btn"
                  title="Re-search LoanDisk and re-link this case to a different loan"
                >
                  <Search className="w-3 h-3 mr-1.5" />
                  Edit loan
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <Tabs value={detailsTab} onValueChange={setDetailsTab}>
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="details" data-testid="bd-tab-details">Details</TabsTrigger>
                  <TabsTrigger value="notes" data-testid="bd-tab-notes">
                    Notes
                    {(edit.notes || "").trim() && (
                      <Badge variant="secondary" className="ml-1.5 text-[9px] h-4 px-1.5">●</Badge>
                    )}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="space-y-3 mt-0">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <Label className="text-xs text-muted-foreground">Customer Name</Label>
                      <Input
                        value={edit.customer_name}
                        readOnly
                        disabled
                        className="bg-slate-50 text-foreground cursor-default"
                        data-testid="bd-edit-name"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">SF# / Loan Ref</Label>
                      <Input
                        value={edit.sfa_number}
                        readOnly
                        disabled
                        className="bg-slate-50 text-foreground cursor-default font-mono"
                        data-testid="bd-edit-sfa"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Write-off Month</Label>
                      <Input
                        value={edit.write_off_month ? fmtMonth(edit.write_off_month) : "—"}
                        readOnly
                        disabled
                        className="bg-slate-50 text-foreground cursor-default"
                        data-testid="bd-edit-month-display"
                      />
                    </div>
                  </div>
                  <p className="text-[10px] text-muted-foreground italic">
                    Auto-pulled from LoanDisk on every daily sync. Use <span className="font-semibold">Edit loan</span> above to re-link to a different SF#.
                  </p>
                </TabsContent>

                <TabsContent value="notes" className="mt-0">
                  <Label className="text-xs">Notes</Label>
                  <Textarea
                    value={edit.notes}
                    onChange={e => setEdit({ ...edit, notes: e.target.value })}
                    placeholder="Settlement terms, debt-collector ref, court dates, customer correspondence, anything ops needs to remember about this case…"
                    rows={10}
                    className="resize-y min-h-[200px] text-sm"
                    data-testid="bd-edit-notes"
                  />
                  <p className="text-[10px] text-muted-foreground italic mt-1.5">
                    Free-form text. Click Save changes at the bottom to persist.
                  </p>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Edit-loan dialog — opened from the header button above */}
          <EditLoanDialog
            open={editLoanOpen}
            onOpenChange={setEditLoanOpen}
            caseId={caseRow.case_id}
            currentRef={caseRow.sfa_number}
            currentCustomer={caseRow.customer_name}
            onRelinked={() => { onChanged(); onClose(); }}
          />

          {/* ───────────── 2. FINANCIAL INFORMATION ───────────── */}
          {/* Principal numbers, computed write-off, AND the LoanDisk borrower
              loan history all in one tile. The history table is embedded
              flat (no nested Card / no collapse) so ops sees the full credit
              context without an extra click. */}
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between gap-3">
              <CardTitle className="text-sm flex items-center gap-2"><PoundSterling className="w-4 h-4" /> Financial Information</CardTitle>
              <Button
                size="sm"
                variant="outline"
                onClick={refreshPrincipal}
                disabled={previewLoading || !edit.sfa_number}
                className="h-7 px-2 text-[11px]"
                data-testid="bd-refresh-principal-btn"
                title="Re-fetch original principal + remaining principal from LoanDisk's restructure chain"
              >
                {previewLoading ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1.5" />}
                Refresh from LoanDisk
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Original Principal £</Label>
                  <Input type="number" step="0.01" value={edit.original_principal} onChange={e => setEdit({ ...edit, original_principal: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Remaining Principal £</Label>
                  <Input type="number" step="0.01" value={edit.remaining_principal} onChange={e => setEdit({ ...edit, remaining_principal: e.target.value })} data-testid="bd-edit-remaining" />
                </div>
              </div>
              {/* Live computed write-off readout + algorithm tooltip */}
              <div className="rounded-md bg-slate-50 border px-3 py-2 flex items-center justify-between text-sm">
                <div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                    Computed write-off £
                    <HoverCard openDelay={150}>
                      <HoverCardTrigger asChild>
                        <button type="button" className="text-muted-foreground hover:text-foreground" data-testid="bd-algo-tooltip-trigger" aria-label="How the principal is calculated">
                          <Info className="w-3 h-3" />
                        </button>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-[440px] text-xs p-3" align="start" data-testid="bd-algo-tooltip">
                        <div className="font-semibold mb-2 text-sm">Algorithm v3 — how we compute remaining principal</div>
                        <p className="text-muted-foreground mb-3 leading-relaxed">
                          We take LoanDisk's current loan balance, subtract anything that isn't real lent principal (outstanding fees, outstanding penalty, and any unpaid fees/penalty rolled over from prior loans via the standard payment waterfall), then divide by the original loan's interest factor to back out the principal portion.
                        </p>
                        {(() => {
                          const src = preview?.ok ? preview : caseRow;
                          const hasBreakdown = src && src.remaining_principal != null && src.adjusted_balance != null;
                          if (!hasBreakdown) {
                            return (
                              <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 px-2 py-2">
                                No breakdown stored yet. Click "Refresh from LoanDisk" above to compute the live values.
                              </div>
                            );
                          }
                          const currentRef = src.current_loan_ref || src.loandisk_latest_ref || src.sfa_number;
                          const originalRef = src.original_loan_ref || src.sfa_number;
                          const carryover = src.chain_carryover_from_prior_loans ?? src.chain_penalty_sum_excluded ?? 0;
                          return (
                            <>
                              <div className="font-mono space-y-1 bg-slate-50 border rounded p-2.5">
                                <div className="flex justify-between"><span>Current balance ({currentRef})</span><span className="font-semibold">{fmtGBPdp(src.current_balance || 0)}</span></div>
                                {carryover > 0 && (
                                  <div className="flex justify-between text-red-700"><span>− Chain carryover (prior loans)</span><span>−{fmtGBPdp(carryover)}</span></div>
                                )}
                                {(src.current_fees_outstanding ?? 0) > 0 && (
                                  <div className="flex justify-between text-red-700"><span>− Outstanding fees</span><span>−{fmtGBPdp(src.current_fees_outstanding)}</span></div>
                                )}
                                {(src.current_penalty_outstanding ?? 0) > 0 && (
                                  <div className="flex justify-between text-red-700"><span>− Outstanding penalty</span><span>−{fmtGBPdp(src.current_penalty_outstanding)}</span></div>
                                )}
                                <div className="flex justify-between border-t pt-1 mt-1"><span>= Adjusted balance</span><span className="font-semibold">{fmtGBPdp(src.adjusted_balance || 0)}</span></div>
                                <div className="flex justify-between"><span>÷ Factor ({src.original_interest_rate_pct ?? "?"}% on {originalRef})</span><span>÷ {src.factor}</span></div>
                                <div className="flex justify-between border-t pt-1 mt-1 text-red-700 font-bold"><span>= Remaining principal</span><span>{fmtGBPdp(src.remaining_principal || 0)}</span></div>
                              </div>
                              {(src.chain_length ?? 0) > 1 && (
                                <div className="mt-2 text-[10px] text-muted-foreground">
                                  Chain: {(src.loandisk_chain_refs || src.chain_refs || []).join(" → ")} ({src.chain_length} loans)
                                </div>
                              )}
                              <div className="mt-2 text-[10px] text-muted-foreground">
                                {preview?.ok ? "Live values (just refreshed)" : `From last sync · ${caseRow.last_loandisk_sync ? caseRow.last_loandisk_sync.slice(0, 10) : "?"}`}
                              </div>
                            </>
                          );
                        })()}
                      </HoverCardContent>
                    </HoverCard>
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Remaining × Write-off % = {fmtGBPdp(parseFloat(edit.remaining_principal) || 0)} × {Math.round((parseFloat(edit.default_pct) || 0) * 100)}%
                  </div>
                </div>
                <div className="text-lg font-bold tabular-nums text-red-700" data-testid="bd-drawer-computed-amount">
                  {fmtGBPdp((parseFloat(edit.remaining_principal) || 0) * (parseFloat(edit.default_pct) || 0))}
                </div>
              </div>

              {/* Borrower loan history embedded inline (no card-in-card, no
                  collapse). Auto-loads on mount from the 24h cache. */}
              <BorrowerLoanHistory sfaNumber={caseRow.sfa_number} caseLoanId={caseRow.loandisk_loan_id} flat />
            </CardContent>
          </Card>

          {/* ───────────── 3. CASE ACTIONS ───────────── */}
          {/* Just the outcome decision. Payments aren't logged manually —
              the daily LoanDisk sync picks up balance changes automatically
              and the algorithm reduces `remaining_principal` accordingly,
              so manual payment logs would only get clobbered on next sync. */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2"><Target className="w-4 h-4" /> Case Actions</CardTitle>
              <p className="text-xs text-muted-foreground">
                Pick where this case should go. "Written Off" locks the case (use Edit loan above to reopen). "Restructured" moves it back to Upcoming Capital at Risk.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <OutcomeSelector
                value={deriveOutcome(edit.status, edit.default_pct, edit.category)}
                defaultPct={Number(edit.default_pct) || 0}
                onChange={(outcomeKey, partialPct) => {
                  const def = OUTCOMES.find(o => o.key === outcomeKey);
                  if (!def) return;
                  const isPartial = def.supportsPartial && partialPct != null && partialPct < 1;
                  setEdit(prev => ({
                    ...prev,
                    status: isPartial ? "partial_settlement" : def.status,
                    default_pct: isPartial ? partialPct : def.default_pct,
                    category: def.category,
                  }));
                }}
              />
            </CardContent>
          </Card>

          {/* ───────────── 4. OPEN IN LOANDISK ───────────── */}
          {ldLink && (
            <Card>
              <CardContent className="pt-4 pb-4 flex items-center justify-between gap-3">
                <div className="text-xs text-muted-foreground">
                  Save changes here, then flip the LoanDisk loan status to match (the daily sync will also reconcile).
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  asChild
                  className="text-blue-700 shrink-0"
                  data-testid="bd-open-loandisk-btn"
                >
                  <a href={ldLink} target="_blank" rel="noreferrer">
                    Open in LoanDisk <ChevronRight className="w-3.5 h-3.5" />
                  </a>
                </Button>
              </CardContent>
            </Card>
          )}
          {/* End of lock-wrappable region — actions footer stays clickable */}
          </div>


          {/* Actions */}
          <div className="flex justify-between gap-2 sticky bottom-0 bg-background pt-3 border-t">
            <Button variant="destructive" size="sm" onClick={del} data-testid="bd-delete-btn">
              <X className="w-4 h-4 mr-1.5" /> Delete
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
              <Button size="sm" onClick={save} disabled={saving || edit.status === "write_off" || edit.status === "partial_settlement"} data-testid="bd-save-btn">
                {saving ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : null}
                Save changes
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Add-case dialog
// ---------------------------------------------------------------------------
function AddCaseDialog({ open, onClose, onSaved }) {
  const [form, setForm] = useState({
    customer_name: "", sfa_number: "", remaining_principal: "",
    default_pct: 1, status: "legal", category: "active", write_off_month: "", notes: "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.customer_name) { toast.error("Customer name required"); return; }
    try {
      setSaving(true);
      await createBadDebtCase({
        ...form,
        default_pct: parseFloat(form.default_pct) || 1,
        remaining_principal: form.remaining_principal ? parseFloat(form.remaining_principal) : null,
      });
      toast.success("Case created");
      onSaved();
    } catch (e) {
      toast.error("Create failed: " + errorDetailMessage(e, e.message));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="bd-add-dialog">
        <DialogHeader>
          <DialogTitle>Add Bad Debt Case</DialogTitle>
          <DialogDescription>Manually create a case. To pull live numbers from LoanDisk, save first then open the case and use the Principal Calculator.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs">Customer Name</Label>
            <Input value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} data-testid="bd-add-name" />
          </div>
          <div>
            <Label className="text-xs">SF#</Label>
            <Input value={form.sfa_number} onChange={e => setForm({ ...form, sfa_number: e.target.value })} placeholder="SF-12705" data-testid="bd-add-sfa" />
          </div>
          <div>
            <Label className="text-xs">Write-off Month</Label>
            <Input type="month" value={form.write_off_month} onChange={e => setForm({ ...form, write_off_month: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Remaining Principal £</Label>
            <Input type="number" step="0.01" value={form.remaining_principal} onChange={e => setForm({ ...form, remaining_principal: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Default % (0–1)</Label>
            <Input type="number" step="0.05" min="0" max="1" value={form.default_pct} onChange={e => setForm({ ...form, default_pct: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Category</Label>
            <select className="w-full h-9 rounded-md border px-2 text-sm bg-white" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
              <option value="active">active (bad debt)</option>
              <option value="upcoming">upcoming (capital at risk)</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <select className="w-full h-9 rounded-md border px-2 text-sm bg-white" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
              {BD_STATUS_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Notes</Label>
            <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="bd-add-save-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : null}
            Create case
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Audit panel — xlsx ↔ LoanDisk reconciliation
// ---------------------------------------------------------------------------
function AuditPanel({ report, open, onToggle, onBackfill, onSeedUpcoming, onClose }) {
  const stats = [
    { label: "Auto-matched", value: report.matched_count, tone: "emerald" },
    { label: "Ambiguous", value: report.ambiguous_count, tone: "amber" },
    { label: "Missing from LoanDisk", value: report.unmatched_xlsx_count, tone: "red" },
  ];
  return (
    <Card className="border-blue-200 bg-blue-50/30" data-testid="bd-audit-panel">
      <CardHeader className="pb-3 cursor-pointer" onClick={onToggle}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-blue-700" />
            Data Audit — xlsx vs LoanDisk
            <span className="text-xs font-normal text-muted-foreground ml-2">click to {open ? "hide" : "show"} details</span>
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onClose(); }} className="h-7 w-7 p-0">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex gap-2 mt-2">
          {stats.map(s => (
            <Badge key={s.label} variant="outline" className={
              s.tone === "emerald" ? "bg-emerald-50 text-emerald-700 border-emerald-300" :
              s.tone === "amber"   ? "bg-amber-50 text-amber-700 border-amber-300" :
                                     "bg-red-50 text-red-700 border-red-300"
            }>
              {s.value} {s.label}
            </Badge>
          ))}
        </div>
      </CardHeader>
      {open && (
        <CardContent className="space-y-4 text-xs">
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={onBackfill} data-testid="bd-audit-backfill-btn">
              Apply {report.matched_count} matches → write_off_month + default %
            </Button>
            <Button size="sm" variant="outline" onClick={onSeedUpcoming} data-testid="bd-audit-seed-upcoming-btn">
              Re-seed upcoming-capital-at-risk from xlsx
            </Button>
          </div>

          {report.ambiguous_count > 0 && (
            <div>
              <h4 className="font-semibold text-amber-800 mb-1">Ambiguous matches ({report.ambiguous_count})</h4>
              <p className="text-muted-foreground mb-2">Low-confidence name pairs — review and link manually via the case drawer.</p>
              <table className="w-full">
                <thead className="bg-amber-100 text-amber-900">
                  <tr><th className="text-left p-1.5">LoanDisk customer</th><th className="text-left p-1.5">SF#</th><th className="text-left p-1.5">Likely xlsx match</th><th className="text-right p-1.5">Score</th></tr>
                </thead>
                <tbody>
                  {(report.ambiguous || []).map((a, i) => (
                    <tr key={i} className="border-b border-amber-100">
                      <td className="p-1.5">{a.ld_name}</td>
                      <td className="p-1.5 text-muted-foreground">{a.ld_sf || "—"}</td>
                      <td className="p-1.5">{a.xlsx_name || "—"}</td>
                      <td className="p-1.5 text-right tabular-nums">{(a.best_score ?? a.score ?? 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {report.unmatched_xlsx_count > 0 && (
            <div>
              <h4 className="font-semibold text-red-800 mb-1">Missing from LoanDisk legal pipeline ({report.unmatched_xlsx_count})</h4>
              <p className="text-muted-foreground mb-2">These customers are in your xlsx but not in LoanDisk's current legal-status loans. Could mean: paid up, restructured, recovered, or different LoanDisk name — investigate.</p>
              <table className="w-full">
                <thead className="bg-red-100 text-red-900">
                  <tr><th className="text-left p-1.5">xlsx customer</th><th className="text-left p-1.5">Month</th><th className="text-right p-1.5">Amount</th><th className="text-right p-1.5">Default %</th></tr>
                </thead>
                <tbody>
                  {(report.unmatched_xlsx || []).map((u, i) => (
                    <tr key={i} className="border-b border-red-100">
                      <td className="p-1.5">{u.xlsx_name}</td>
                      <td className="p-1.5 text-muted-foreground">{fmtMonth(u.xlsx_month)}</td>
                      <td className="p-1.5 text-right tabular-nums">{fmtGBP(u.xlsx_amount)}</td>
                      <td className="p-1.5 text-right tabular-nums">{fmtPct(u.xlsx_default_pct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function ProvisionDialog({ open, onClose, onSaved }) {
  const [form, setForm] = useState({ month: "", provision_amount: "", originations: "", notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const now = new Date();
      const m = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      setForm({ month: m, provision_amount: "", originations: "", notes: "" });
    }
  }, [open]);

  const save = async () => {
    if (!form.month || !form.provision_amount) { toast.error("Month & provision amount required"); return; }
    try {
      setSaving(true);
      await upsertBadDebtProvision({
        month: form.month,
        provision_amount: parseFloat(form.provision_amount),
        originations: form.originations ? parseFloat(form.originations) : null,
        notes: form.notes,
      });
      toast.success("Provision saved");
      onSaved();
    } catch (e) {
      toast.error("Save failed: " + errorDetailMessage(e, e.message));
    } finally { setSaving(false); }
  };

  // Auto-compute 7% if originations entered
  const suggested = useMemo(() => {
    if (!form.originations) return null;
    return parseFloat(form.originations) * 0.07;
  }, [form.originations]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="bd-provision-dialog">
        <DialogHeader>
          <DialogTitle>Monthly Provision</DialogTitle>
          <DialogDescription>7% of the month's originations. Manual entry — published from sales.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Month</Label>
            <Input type="month" value={form.month} onChange={e => setForm({ ...form, month: e.target.value })} data-testid="bd-provision-month" />
          </div>
          <div>
            <Label className="text-xs">Originations £ (optional, just for the suggestion)</Label>
            <Input type="number" step="1" value={form.originations} onChange={e => setForm({ ...form, originations: e.target.value })} placeholder="e.g. 2000000" />
            {suggested != null && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Suggested provision @ 7% = <strong className="text-emerald-700">{fmtGBP(suggested)}</strong>
                <Button variant="link" size="sm" className="h-auto p-0 ml-2 text-emerald-700" onClick={() => setForm({ ...form, provision_amount: suggested.toFixed(2) })}>use this</Button>
              </p>
            )}
          </div>
          <div>
            <Label className="text-xs">Provision £</Label>
            <Input type="number" step="0.01" value={form.provision_amount} onChange={e => setForm({ ...form, provision_amount: e.target.value })} data-testid="bd-provision-amount" />
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="bd-provision-save-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : null}
            Save provision
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
